/* SuperHoodie – hoodie / onesie configurator.
   Ported on 2026-10-19 from Gesicht auf Socken (assets/sh-configurator.js, state 2026-10-18) to bring the AI styles
   and the live preview on the product to SuperHoodie. Customer texts are UK English; the German comments inside are
   kept from there on purpose, so both files can be diffed and future fixes carried over.
   No app dependency: the photo files are posted as native line-item properties ("properties[Photo]",
   "properties[Second photo]") to /cart/add.js; add-ons are added as their own products.
   AI styles (section setting "Offer drawn styles"): before the upload the customer picks "Real photo" or a drawn style
   (Cartoon, Comic, Watercolour, Pop art). The image service (services/ai-api, art=kopf, stil=…) draws after the upload,
   up to aiMaxTries tries per photo and style; the item can go to the basket as soon as the pictures are ready (no approval
   step since 2026-11-03, Joel: „der schritt freigabe steht zwischen dem kauf und stört“). Extra properties then:
   "Style", "Artwork" and, with two faces, "Second artwork" (PNG = print file). */
(function () {
  'use strict';

  var MAX_BYTES = 20 * 1024 * 1024;
  var MIN_SIDE = 800;
  var ALLOWED = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/webp'];
  // Logo hoodie: the formats the old site accepted as well (the producer prints from the original file)
  var ALLOWED_LOGO = ALLOWED.concat(['image/gif', 'image/svg+xml', 'application/pdf', 'application/postscript', 'application/illustrator']);
  var LOGO_EXT = /\.(jpe?g|png|gif|webp|heic|heif|svg|pdf|ai)$/i;

  function formatMoney(cents, format) {
    var amount = cents / 100;
    var value = cents % 100 === 0 ? String(Math.round(amount)) : amount.toFixed(2);
    value = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '£{{amount}}').replace(/\{\{\s*[a-z_]+\s*\}\}/, value);
  }

  function sizedUrl(src, width) {
    if (!src) return '';
    var url = src.indexOf('//') === 0 ? 'https:' + src : src;
    url = url.replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function isHeic(file) {
    return /\.(heic|heif)$/i.test(file.name) || file.type === 'image/heic' || file.type === 'image/heif';
  }

  // Dateien, die ein Browser nicht als <img> zeigen kann (PDF, Illustrator)
  function isDocument(file) {
    return /\.(pdf|ai)$/i.test(file.name) || file.type === 'application/pdf' || file.type === 'application/postscript' || file.type === 'application/illustrator';
  }

  function isSvg(file) {
    return /\.svg$/i.test(file.name) || file.type === 'image/svg+xml';
  }

  function capitalise(text) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  /* KI-Stil: Kopie des Fotos, auf max. 1024 px verkleinert, als JPEG für den Bild-Dienst (das Original geht
     mit der Bestellung). Kann der Browser die Datei nicht lesen (z. B. HEIC), geht das Original. */
  function apiPhoto(file, url) {
    return new Promise(function (resolve) {
      var img = new Image();
      img.onload = function () {
        try {
          var scale = Math.min(1, 1024 / Math.max(img.naturalWidth, img.naturalHeight));
          var canvas = document.createElement('canvas');
          canvas.width = Math.round(img.naturalWidth * scale);
          canvas.height = Math.round(img.naturalHeight * scale);
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(function (blob) { resolve(blob || file); }, 'image/jpeg', 0.9);
        } catch (e) { resolve(file); }
      };
      img.onerror = function () { resolve(file); };
      img.src = url;
    });
  }

  function base64ToBlob(b64, type) {
    var bin = atob(b64);
    var bytes = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: type });
  }

  /* Zuschnitt (Section-Einstellung „Zuschnitt“, Fotodecke 3:4) wie früher im Uploadery-Editor: Man verschiebt und
     zoomt das Foto in einem festen Rahmen, gedruckt wird genau dieser Ausschnitt. Er geht als JPEG
     „editor-<name>.jpeg“ an Shopify (wie bei Uploadery; Shopify vergibt beim Speichern einen eigenen Namen). Sehr große Fotos werden auf CROP_MAX_PIXELS
     verkleinert, weil iOS-Safari größere Canvas nicht zeichnet. Ergebnis: { file, rect } oder null (abgebrochen);
     scheitert, wenn der Browser das Foto nicht öffnen kann (z. B. HEIC in Chrome). */
  var CROP_MAX_PIXELS = 16000000;
  var cropUi = null;

  function cropDialog(file, ratio, rect) {
    if (!cropUi) {
      var d = document.createElement('dialog');
      d.className = 'shp-crop';
      d.setAttribute('aria-labelledby', 'ShpCropTitle');
      d.innerHTML = '<div class="shp-crop__box">' +
        '<h2 class="shp-crop__title" id="ShpCropTitle">Choose the crop</h2>' +
        '<p class="shp-crop__text">Move and zoom your photo until everything important is inside the frame. We print exactly this crop.</p>' +
        '<div class="shp-crop__frame" tabindex="0" role="img" aria-label="Crop. Move with the arrow keys, zoom with plus and minus."><img class="shp-crop__img" alt="" draggable="false"></div>' +
        '<label class="shp-crop__zoom"><span>Zoom</span><input type="range" min="1" max="4" step="0.01" value="1"></label>' +
        '<div class="shp-crop__actions"><button type="button" class="sh-btn sh-btn--light" data-crop-cancel>Cancel</button>' +
        '<button type="button" class="sh-btn sh-btn--primary" data-crop-ok>Use this crop</button></div>' +
        '</div>';
      document.body.appendChild(d);
      cropUi = { dialog: d, frame: d.querySelector('.shp-crop__frame'), img: d.querySelector('.shp-crop__img'), zoom: d.querySelector('input'), ok: d.querySelector('[data-crop-ok]'), cancel: d.querySelector('[data-crop-cancel]') };
    }
    var ui = cropUi;
    var url = URL.createObjectURL(file);
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('crop')); };
      img.src = url;
    }).then(function (img) {
      var nw = img.naturalWidth, nh = img.naturalHeight;
      var W = 0, H = 0, base = 1, z = 1, cx = nw / 2, cy = nh / 2;
      if (rect) { z = Math.max(1, Math.min(4, Math.min(nw / rect.w, nh / rect.h))); cx = rect.x + rect.w / 2; cy = rect.y + rect.h / 2; }
      ui.frame.style.aspectRatio = ratio[0] + ' / ' + ratio[1];
      ui.img.src = url;

      function scale() { return base * z; }
      function clamp() {
        var s = scale();
        cx = Math.min(Math.max(cx, W / 2 / s), nw - W / 2 / s);
        cy = Math.min(Math.max(cy, H / 2 / s), nh - H / 2 / s);
      }
      function render() {
        clamp();
        var s = scale();
        // Angezeigte Größe statt scale(): sehr große Fotos als Ebene in voller Größe überfordern Handys
        ui.img.style.width = nw * s + 'px';
        ui.img.style.height = nh * s + 'px';
        ui.img.style.transform = 'translate(' + (W / 2 - cx * s) + 'px,' + (H / 2 - cy * s) + 'px)';
        ui.zoom.value = z;
      }
      function layout() {
        var box = ui.frame.getBoundingClientRect();
        W = box.width; H = box.height;
        base = Math.max(W / nw, H / nh);
        render();
      }
      // Zoomen um einen Punkt im Rahmen (Rahmen-Koordinaten), der Bildpunkt darunter bleibt stehen
      function zoomTo(nz, px, py) {
        nz = Math.min(4, Math.max(1, nz));
        if (px == null) { px = W / 2; py = H / 2; }
        var s0 = scale();
        var ix = cx + (px - W / 2) / s0, iy = cy + (py - H / 2) / s0;
        z = nz;
        var s1 = scale();
        cx = ix - (px - W / 2) / s1;
        cy = iy - (py - H / 2) / s1;
        render();
      }
      function local(e) {
        var box = ui.frame.getBoundingClientRect();
        return { x: e.clientX - box.left, y: e.clientY - box.top };
      }

      var pointers = {};
      var pinch = null;
      function onDown(e) {
        ui.frame.setPointerCapture(e.pointerId);
        pointers[e.pointerId] = local(e);
        pinch = null;
      }
      function onMove(e) {
        var prev = pointers[e.pointerId];
        if (!prev) return;
        var p = local(e);
        pointers[e.pointerId] = p;
        var ids = Object.keys(pointers);
        if (ids.length === 1) {
          cx -= (p.x - prev.x) / scale();
          cy -= (p.y - prev.y) / scale();
          render();
        } else if (ids.length === 2) {
          var a = pointers[ids[0]], b = pointers[ids[1]];
          var dist = Math.hypot(a.x - b.x, a.y - b.y);
          if (pinch && pinch.dist > 0) zoomTo(z * dist / pinch.dist, (a.x + b.x) / 2, (a.y + b.y) / 2);
          pinch = { dist: dist };
        }
      }
      function onUp(e) { delete pointers[e.pointerId]; pinch = null; }
      function onWheel(e) {
        e.preventDefault();
        var p = local(e);
        zoomTo(z * Math.exp(-e.deltaY * 0.002), p.x, p.y);
      }
      function onKey(e) {
        var step = (e.shiftKey ? 40 : 10) / scale();
        if (e.key === 'ArrowLeft') cx -= step;
        else if (e.key === 'ArrowRight') cx += step;
        else if (e.key === 'ArrowUp') cy -= step;
        else if (e.key === 'ArrowDown') cy += step;
        else if (e.key === '+' || e.key === '=') { zoomTo(z * 1.1); e.preventDefault(); return; }
        else if (e.key === '-') { zoomTo(z / 1.1); e.preventDefault(); return; }
        else return;
        e.preventDefault();
        render();
      }
      function onZoom() { zoomTo(parseFloat(ui.zoom.value)); }

      return new Promise(function (resolve) {
        var done = false;
        function finish(result) {
          if (done) return;
          done = true;
          ui.frame.removeEventListener('pointerdown', onDown);
          ui.frame.removeEventListener('pointermove', onMove);
          ui.frame.removeEventListener('pointerup', onUp);
          ui.frame.removeEventListener('pointercancel', onUp);
          ui.frame.removeEventListener('wheel', onWheel);
          ui.frame.removeEventListener('keydown', onKey);
          ui.zoom.removeEventListener('input', onZoom);
          ui.ok.removeEventListener('click', onOk);
          ui.cancel.removeEventListener('click', onCancel);
          ui.dialog.removeEventListener('cancel', onCancel);
          window.removeEventListener('resize', layout);
          if (ui.dialog.open) ui.dialog.close();
          ui.img.removeAttribute('src');
          URL.revokeObjectURL(url);
          resolve(result);
        }
        function onCancel(e) { if (e) e.preventDefault(); finish(null); }
        function onOk() {
          var s = scale();
          var r = { x: cx - W / 2 / s, y: cy - H / 2 / s, w: W / s, h: H / s };
          var k = Math.min(1, Math.sqrt(CROP_MAX_PIXELS / (r.w * r.h)));
          var canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(r.w * k));
          canvas.height = Math.max(1, Math.round(canvas.width * ratio[1] / ratio[0]));
          var ctx = canvas.getContext('2d');
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, r.x, r.y, r.w, r.h, 0, 0, canvas.width, canvas.height);
          ui.ok.disabled = true;
          canvas.toBlob(function (blob) {
            ui.ok.disabled = false;
            if (!blob) { finish(null); return; }
            var name = 'editor-' + (file.name.replace(/\.[^.]*$/, '') || 'photo') + '.jpeg';
            var out;
            try { out = new File([blob], name, { type: 'image/jpeg' }); } catch (err) { out = blob; out.name = name; }
            out.shpCropped = true;
            finish({ file: out, rect: r });
          }, 'image/jpeg', 0.95);
        }
        ui.frame.addEventListener('pointerdown', onDown);
        ui.frame.addEventListener('pointermove', onMove);
        ui.frame.addEventListener('pointerup', onUp);
        ui.frame.addEventListener('pointercancel', onUp);
        ui.frame.addEventListener('wheel', onWheel, { passive: false });
        ui.frame.addEventListener('keydown', onKey);
        ui.zoom.addEventListener('input', onZoom);
        ui.ok.addEventListener('click', onOk);
        ui.cancel.addEventListener('click', onCancel);
        ui.dialog.addEventListener('cancel', onCancel);
        window.addEventListener('resize', layout);
        ui.dialog.showModal();
        layout();
        ui.frame.focus();
      });
    }, function (err) {
      URL.revokeObjectURL(url);
      throw err;
    });
  }

  /* Vorschau fertig (Joel 2026-10-15): Ist das Produktbild nicht im Blick, erscheint oben kurz eine Pille
     „Deine Vorschau ist fertig – ansehen“; ein Tipp scrollt zum Bild. Sie verschwindet, sobald das Bild zu sehen
     ist, nach READY_MS oder nach dem Tipp. Eine Pille für die ganze Seite. */
  var READY_MS = 8000;
  var ARROW_UP = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 19V5M5 12l7-7 7 7"/></svg>';
  var readyUi = null;

  function scrollToStage(box) {
    if (!box) return;
    var smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    box.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
  }

  function hideReady() {
    if (!readyUi) return;
    readyUi.btn.hidden = true;
    clearTimeout(readyUi.timer);
    if (readyUi.observer) readyUi.observer.disconnect();
  }

  function showReady(box) {
    if (!readyUi) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'shp-ready';
      b.hidden = true;
      b.innerHTML = ARROW_UP + '<span>Your preview is ready – take a look</span>';
      document.body.appendChild(b);
      readyUi = { btn: b, timer: null, observer: null, box: null };
      b.addEventListener('click', function () { scrollToStage(readyUi.box); hideReady(); });
    }
    hideReady();
    readyUi.box = box;
    readyUi.btn.hidden = false;
    readyUi.timer = setTimeout(hideReady, READY_MS);
    if ('IntersectionObserver' in window) {
      readyUi.observer = new IntersectionObserver(function (entries) {
        if (entries[0].intersectionRatio >= 0.4) hideReady();
      }, { threshold: [0.4] });
      readyUi.observer.observe(box);
    }
  }

  // Bot-Schutz (Cloudflare Turnstile) wie beim Kissen, nur mit Site-Key
  var turnstileLoad = null;
  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (!turnstileLoad) {
      turnstileLoad = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        s.async = true;
        s.onload = function () { window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile')); };
        s.onerror = function () { turnstileLoad = null; reject(new Error('turnstile')); };
        document.head.appendChild(s);
      });
    }
    return turnstileLoad;
  }
  var TURNSTILE_ERROR = 'We couldn’t check your browser just now. Please reload the page and try again.';
  var AI_ERROR = 'We couldn’t create your picture just now. Please try again.';

  function newFace() {
    return { status: 'none', tries: 0, blob: null, url: '', err: '' };
  }

  function Configurator(root) {
    this.root = root;
    this.product = JSON.parse(root.querySelector('[data-shp-product]').textContent);
    this.config = JSON.parse(root.querySelector('[data-shp-config]').textContent);
    this.files = { 1: null, 2: null };
    this.urls = { 1: null, 2: null };
    this.previewable = { 1: true, 2: true };
    this.originals = { 1: null, 2: null }; // Zuschnitt: Originalfoto und gewählter Ausschnitt, für „Ausschnitt“
    this.noun = this.config.noun || 'photo';
    this.busy = false;
    this.view = 'variant';
    this.viewModel = false;
    this.ai = !!this.config.ai;
    this.style = 'foto';
    this.art = {}; // Stil → { 1: Bild zum ersten Foto, 2: zum zweiten }
    this.turnstile = { id: null, pending: null };

    var initialId = parseInt(root.querySelector('[data-shp-variant]').value, 10);
    var initial = this.product.variants.filter(function (v) { return v.id === initialId; })[0] || this.product.variants[0];
    this.options = initial.options.slice();
    if (this.config.sizeUnset && this.config.sizeIndex > -1) this.options[this.config.sizeIndex] = null;

    this.bind();
    this.addViewLinks();
    this.watchBar();
    this.update();
    // Live-Vorschau auf der Socke (sh-sock-preview.js, nur wenn in der Section eingeschaltet und geladen)
    if (window.GasSockPreview) window.GasSockPreview.attach(this);
  }

  // this.cartoon bezieht sich immer auf den gewählten Stil
  Object.defineProperty(Configurator.prototype, 'cartoon', {
    get: function () {
      if (!this.art[this.style]) this.art[this.style] = { 1: newFace(), 2: newFace() };
      return this.art[this.style];
    }
  });

  Configurator.prototype.$ = function (sel) { return this.root.querySelector(sel); };
  Configurator.prototype.$$ = function (sel) { return Array.prototype.slice.call(this.root.querySelectorAll(sel)); };

  Configurator.prototype.bind = function () {
    var self = this;

    this.$$('[data-opt]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var i = parseInt(btn.getAttribute('data-opt'), 10);
        self.options[i] = btn.getAttribute('data-value');
        if (i === self.config.colourIndex) { self.view = 'variant'; self.viewModel = false; }
        self.update();
      });
    });

    this.$$('[data-shp-thumb]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.view = btn.getAttribute('data-shp-thumb');
        self.viewModel = btn.hasAttribute('data-shp-model');
        self.$$('[data-shp-thumb]').forEach(function (b) { b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'); });
        self.renderStage();
      });
    });

    // „+ N weitere“: die übrigen Motive aufklappen
    this.$$('[data-shp-more-btn]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var more = self.$$('[data-shp-more]');
        more.forEach(function (chip) { chip.hidden = false; });
        btn.remove();
        if (more[0]) more[0].focus();
      });
    });

    // Themen (viele Motive): ein Thema aufklappen, die anderen zu; erneuter Klick oder „Schließen“ klappt zu
    var themeBtns = this.$$('[data-shp-theme]');
    var themePanels = this.$$('[data-shp-theme-panel]');
    function openTheme(n) {
      themeBtns.forEach(function (b) { b.setAttribute('aria-expanded', b.getAttribute('data-shp-theme') === n ? 'true' : 'false'); });
      themePanels.forEach(function (p) { p.hidden = p.getAttribute('data-shp-theme-panel') !== n; });
    }
    themeBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        openTheme(btn.getAttribute('aria-expanded') === 'true' ? null : btn.getAttribute('data-shp-theme'));
      });
    });
    this.$$('[data-shp-theme-close]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var n = btn.closest('[data-shp-theme-panel]').getAttribute('data-shp-theme-panel');
        openTheme(null);
        themeBtns.forEach(function (b) { if (b.getAttribute('data-shp-theme') === n) b.focus(); });
      });
    });

    this.$$('[data-shp-file]').forEach(function (input) {
      input.addEventListener('change', function () {
        var n = parseInt(input.getAttribute('data-shp-file'), 10);
        if (input.files && input.files[0]) self.setFile(n, input.files[0], input);
      });
    });

    this.$$('[data-shp-recrop]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var n = parseInt(btn.getAttribute('data-shp-recrop'), 10);
        var o = self.originals[n];
        if (o) self.cropFile(n, o.file, null, o.rect);
      });
    });

    this.$$('[data-shp-replace]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var input = self.$('[data-shp-file="' + btn.getAttribute('data-shp-replace') + '"]');
        if (input) input.click();
      });
    });

    this.$$('[data-shp-remove]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.clearFile(parseInt(btn.getAttribute('data-shp-remove'), 10));
      });
    });

    this.$$('[data-shp-extra]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        btn.setAttribute('aria-pressed', btn.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
        self.update();
      });
    });

    this.$$('[data-shp-add]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (self.missing) self.goToMissing(self.missing);
        else self.addToCart();
      });
    });

    if (!this.ai) return;
    this.$$('[data-shp-style]').forEach(function (btn) {
      btn.addEventListener('click', function () { self.setStyle(btn.getAttribute('data-shp-style')); });
    });
    this.$$('[data-shp-ai-retry]').forEach(function (btn) {
      btn.addEventListener('click', function () { self.generate(parseInt(btn.getAttribute('data-shp-ai-retry'), 10)); });
    });
    var again = this.$('[data-shp-ai-again]');
    if (again) again.addEventListener('click', function () {
      // Neuer Anlauf nach einem Fehler, ohne einen Versuch zu verbrauchen
      self.neededFaces().forEach(function (n) {
        if (self.cartoon[n].status === 'error') { self.cartoon[n].status = 'none'; }
      });
      self.update();
    });
    if (this.config.aiTurnstile) loadTurnstile().catch(function () {});
  };

  /* ---------- KI-Stil ---------- */

  Configurator.prototype.neededFaces = function () {
    return this.needsTwo() ? [1, 2] : [1];
  };

  Configurator.prototype.isCartoon = function () {
    // gezeichneter Stil gewählt (Cartoon, Comic, Aquarell, Pop-Art)
    return this.ai && this.style !== 'foto';
  };

  Configurator.prototype.cartoonsReady = function () {
    var self = this;
    return this.neededFaces().every(function (n) { return self.cartoon[n].status === 'done'; });
  };

  Configurator.prototype.cartoonsBusy = function () {
    var self = this;
    return this.neededFaces().some(function (n) { return self.cartoon[n].status === 'busy'; });
  };

  Configurator.prototype.setStyle = function (style) {
    if (style === this.style) return;
    this.style = style;
    this.announce('Style ' + this.styleName() + ' selected.');
    this.update();
  };

  Configurator.prototype.styleTile = function () {
    return this.$('[data-shp-style="' + this.style + '"]');
  };
  Configurator.prototype.styleName = function () {
    var t = this.styleTile();
    return (t && t.getAttribute('data-name')) || 'Cartoon';
  };
  // "your cartoon", "your comic", "your pop art picture" …
  Configurator.prototype.styleNoun = function () {
    var t = this.styleTile();
    return (t && t.getAttribute('data-noun')) || 'your picture';
  };

  // Neues Foto: alle gezeichneten Bilder zu diesem Foto verwerfen
  Configurator.prototype.resetFace = function (n) {
    var self = this;
    Object.keys(this.art).forEach(function (st) {
      var c = self.art[st][n];
      if (c && c.url) URL.revokeObjectURL(c.url);
      self.art[st][n] = newFace();
    });
  };

  Configurator.prototype.turnstileToken = function () {
    var self = this;
    var box = this.$('[data-shp-turnstile]');
    var key = this.config.aiTurnstile;
    if (!key || !box) return Promise.resolve('');
    return loadTurnstile().then(function (ts) {
      return new Promise(function (resolve, reject) {
        var timer = setTimeout(function () { finish(new Error(TURNSTILE_ERROR)); }, 60000);
        function finish(err, token) {
          clearTimeout(timer);
          self.turnstile.pending = null;
          if (err) reject(err); else resolve(token);
        }
        self.turnstile.pending = finish;
        if (self.turnstile.id === null) {
          self.turnstile.id = ts.render(box, {
            sitekey: key,
            action: 'cartoon',
            appearance: 'interaction-only',
            execution: 'execute',
            retry: 'never',
            language: 'en',
            callback: function (token) { if (self.turnstile.pending) self.turnstile.pending(null, token); },
            'error-callback': function () { if (self.turnstile.pending) self.turnstile.pending(new Error(TURNSTILE_ERROR)); return true; },
            'expired-callback': function () {}
          });
        } else {
          ts.reset(self.turnstile.id);
        }
        ts.execute(self.turnstile.id);
      });
    }, function () { throw new Error(TURNSTILE_ERROR); });
  };

  /* Cartoon für Foto n zeichnen lassen (beim ersten Mal automatisch, danach über „Neu zeichnen“). */
  Configurator.prototype.generate = function (n) {
    var self = this;
    var file = this.files[n];
    var faces = this.cartoon;
    var c = faces[n];
    var stil = this.style;
    if (!file || c.status === 'busy' || c.tries >= this.config.aiMaxTries) return;
    c.status = 'busy';
    c.err = '';
    this.update();
    Promise.all([apiPhoto(file, this.urls[n]), this.turnstileToken()]).then(function (r) {
      var fd = new FormData();
      fd.append('photo', r[0], r[0] === file ? file.name : 'photo.jpg');
      fd.append('art', self.config.aiArt || 'kopf');
      fd.append('stil', stil);
      if (r[1]) fd.append('turnstile', r[1]);
      return fetch(self.config.aiEndpoint, { method: 'POST', body: fd });
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (json) {
        var b64 = json.data && json.data[0] && json.data[0].b64_json;
        if (!res.ok || !b64) throw new Error(json.error || AI_ERROR);
        return base64ToBlob(b64, 'image/png');
      });
    }).then(function (blob) {
      if (faces[n] !== c) return; // Foto inzwischen geändert
      if (c.url) URL.revokeObjectURL(c.url);
      c.blob = blob;
      c.url = URL.createObjectURL(blob);
      c.tries += 1;
      c.status = 'done';
      self.announce(n === 1 ? 'Your picture is ready.' : 'Your second picture is ready.');
    }).catch(function (err) {
      if (faces[n] !== c) return;
      c.status = 'error';
      c.err = err && err.message ? err.message : AI_ERROR;
    }).then(function () {
      self.update();
    });
  };

  /* Fehlende Cartoons anstoßen, sobald „Cartoon“ gewählt ist und die Fotos da sind. */
  Configurator.prototype.ensureCartoons = function () {
    if (!this.isCartoon()) return;
    var self = this;
    this.neededFaces().forEach(function (n) {
      // Auch ohne Vorschau (z. B. HEIC in Safari-fremden Browsern): der Dienst meldet dann ggf. das Format
      if (self.files[n] && self.cartoon[n].status === 'none') self.generate(n);
    });
  };

  Configurator.prototype.renderAi = function () {
    if (!this.ai) return;
    var self = this;
    var step = this.$('[data-shp-style-step]');
    if (!step) return;
    var cartoon = this.isCartoon();
    var hasPhoto = !!this.files[1];
    var faces = this.neededFaces();
    var ready = this.cartoonsReady();
    var busy = this.cartoonsBusy();
    var errors = faces.filter(function (n) { return self.cartoon[n].status === 'error'; });

    this.$$('[data-shp-style]').forEach(function (b) {
      b.setAttribute('aria-pressed', b.getAttribute('data-shp-style') === self.style ? 'true' : 'false');
    });
    // Kacheln: ohne Foto Beispielbilder, danach das eigene Foto bzw. das eigene gezeichnete Bild
    function setImg(img, url) {
      if (!img) return;
      if (!img.getAttribute('data-example')) img.setAttribute('data-example', img.getAttribute('src'));
      var want = url || img.getAttribute('data-example');
      if (img.getAttribute('src') !== want) { img.removeAttribute('srcset'); img.src = want; }
    }
    var ownPhoto = hasPhoto && this.previewable[1] ? this.urls[1] : '';
    this.$$('[data-shp-style]').forEach(function (tile) {
      var st = tile.getAttribute('data-shp-style');
      var ex = self.$('[data-shp-style-ex="' + st + '"]');
      var img = self.$('[data-shp-style-img="' + st + '"]');
      if (st === 'foto') {
        setImg(img, ownPhoto);
        if (ex) ex.hidden = !!ownPhoto;
        return;
      }
      var f1 = self.art[st] && self.art[st][1];
      var status = f1 ? f1.status : 'none';
      setImg(img, status === 'done' ? f1.url : '');
      if (ex) ex.hidden = status === 'done';
      var sub = self.$('[data-shp-style-sub="' + st + '"]');
      if (!sub) return;
      if (!sub.getAttribute('data-default')) sub.setAttribute('data-default', sub.textContent);
      sub.textContent = !hasPhoto ? sub.getAttribute('data-default')
        : status === 'busy' ? 'Drawing …'
        : status === 'error' ? 'Didn’t work'
        : status === 'done' ? 'Ready'
        : sub.getAttribute('data-default');
      sub.classList.toggle('is-ok', hasPhoto && status === 'done');
      // Unter dem Namen nur der Zustand (gezeichnet, fertig), keine Beschreibung
      sub.hidden = !sub.textContent;
    });
    var busyTitle = this.$('[data-shp-ai-busytitle]');
    if (busyTitle) busyTitle.textContent = 'We’re drawing ' + this.styleNoun() + ' …';
    var num = this.$('[data-shp-style-num]');
    if (num) num.classList.toggle('is-done', hasPhoto && (!cartoon || ready));

    var box = this.$('[data-shp-ai]');
    if (box) box.hidden = !cartoon || !hasPhoto;
    var busyEl = this.$('[data-shp-ai-busy]');
    if (busyEl) busyEl.hidden = !busy;
    var anyDone = faces.some(function (n) { return self.cartoon[n].status === 'done'; });
    var heads = this.$('[data-shp-ai-heads]');
    if (heads) heads.hidden = !anyDone;
    for (var n = 1; n <= 2; n++) {
      var c = this.cartoon[n];
      var fig = this.$('[data-shp-ai-head="' + n + '"]');
      if (!fig) continue;
      var show = faces.indexOf(n) > -1 && c.status === 'done';
      fig.hidden = !show;
      if (!show) continue;
      var img = this.$('[data-shp-ai-img="' + n + '"]');
      if (img && img.getAttribute('src') !== c.url) img.src = c.url;
      var retry = this.$('[data-shp-ai-retry="' + n + '"]');
      if (retry) {
        var left = this.config.aiMaxTries - c.tries;
        retry.disabled = left <= 0 || busy;
        retry.textContent = left <= 0 ? 'No tries left' : 'Redraw (' + left + ' left)';
      }
    }
    var ok = this.$('[data-shp-ai-ok]');
    if (ok) ok.hidden = !ready;
    var err = this.$('[data-shp-ai-err]');
    if (err) {
      err.hidden = !errors.length || busy;
      var errText = this.$('[data-shp-ai-errtext]');
      if (errText && errors.length) errText.textContent = this.cartoon[errors[0]].err;
    }
    var cut = this.$('[data-shp-check-cut]');
    if (cut) cut.textContent = cut.getAttribute(cartoon ? 'data-cartoon' : 'data-photo');
  };

  /* Kaufleiste (nur auf dem Handy sichtbar, siehe sh-product.css): nur zeigen, solange der Hauptbutton
     noch weiter unten liegt (danach verdeckt sie sonst Inhalte). */
  Configurator.prototype.watchBar = function () {
    var bar = this.$('[data-shp-bar]');
    var main = this.$('[data-shp-main]');
    if (!bar) return;
    if (!main || !('IntersectionObserver' in window)) { bar.classList.add('is-visible'); return; }
    var mobile = window.matchMedia('(max-width: 900px)');
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var below = entry.boundingClientRect.top > 0;
        bar.classList.toggle('is-visible', mobile.matches && !entry.isIntersecting && below);
      });
    }, { threshold: 0 });
    io.observe(main);
  };

  /* Fehlt noch etwas, springt der Button der Kaufleiste zum offenen Schritt, statt ausgegraut zu sein. */
  Configurator.prototype.goToMissing = function (what) {
    var target = what === 'size' ? this.$('[data-shp-size-step]')
      : what === 'style' ? this.$('[data-shp-style-step]')
      : this.$('[data-shp-slot="' + what + '"]');
    // Foto schon da: zur Ladeanzeige bzw. zum Fehler beim gezeichneten Bild statt zur Stil-Wahl darüber
    var ai = what === 'style' ? this.$('[data-shp-ai]') : null;
    if (ai && !ai.hidden) target = ai;
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (target === ai) {
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        target.style.animation = 'none';
        void target.offsetWidth;
        target.style.animation = 'shp-nudge 1.6s ease';
      }
    } else if (what === 'style') {
      target.classList.remove('is-nudge');
      void target.offsetWidth;
      target.classList.add('is-nudge');
    }
  };

  Configurator.prototype.variantFor = function (options) {
    return this.product.variants.filter(function (v) {
      return v.options.every(function (o, i) { return o === options[i]; });
    })[0] || null;
  };

  /* Günstigster Preis über alle Größen bei sonst gleicher Auswahl (für „ab“, solange keine Größe gewählt ist). */
  Configurator.prototype.minAcrossSizes = function () {
    var self = this;
    var si = this.config.sizeIndex;
    var best = null;
    this.product.variants.forEach(function (v) {
      var match = v.options.every(function (o, i) { return i === si || o === self.options[i]; });
      if (match && (!best || v.price < best.price)) best = v;
    });
    return best;
  };

  Configurator.prototype.needsTwo = function () {
    var i = this.config.facesIndex;
    var values = this.config.facesValues || [];
    return i > -1 && values.indexOf(this.options[i]) > 0;
  };

  Configurator.prototype.sizeMissing = function () {
    return this.config.sizeIndex > -1 && !this.options[this.config.sizeIndex];
  };

  Configurator.prototype.extrasTotal = function () {
    return this.$$('[data-shp-extra][aria-pressed="true"]').reduce(function (sum, b) {
      return sum + parseInt(b.getAttribute('data-price'), 10);
    }, 0);
  };

  Configurator.prototype.announce = function (text) {
    var el = this.$('[data-shp-status]');
    if (el) el.textContent = text || '';
  };

  Configurator.prototype.setMessage = function (n, text, isError) {
    var msg = this.$('[data-shp-msg="' + n + '"]');
    if (!msg) return;
    msg.textContent = text || '';
    msg.hidden = !text;
    msg.classList.toggle('is-error', !!isError);
  };

  Configurator.prototype.setFile = function (n, file, input) {
    var self = this;
    var noun = this.noun;
    var logo = !!this.config.logo;
    var typeOk = logo
      ? ALLOWED_LOGO.indexOf(file.type) > -1 || LOGO_EXT.test(file.name)
      : ALLOWED.indexOf(file.type) > -1 || isHeic(file);
    if (!typeOk) {
      input.value = '';
      this.setMessage(n, logo ? 'Please choose your logo as JPG, PNG, GIF, PDF, SVG or AI file.' : 'Please choose a photo in JPG, PNG, HEIC or WebP format.', true);
      return;
    }
    if (file.size > MAX_BYTES) {
      input.value = '';
      this.setMessage(n, 'This ' + noun + ' is larger than 20 MB. Please choose a smaller one.', true);
      return;
    }
    // Zuschnitt (Fotodecke): erst den Ausschnitt wählen, weiter geht es mit dem zugeschnittenen Bild
    if (this.config.crop && !logo && !file.shpCropped) {
      this.cropFile(n, file, input);
      return;
    }

    if (this.urls[n]) URL.revokeObjectURL(this.urls[n]);
    this.files[n] = file;
    this.urls[n] = URL.createObjectURL(file);
    this.setMessage(n, '', false);
    if (this.ai) this.resetFace(n);

    var name = this.$('[data-shp-name="' + n + '"]');
    if (name) name.textContent = file.shpCropped && this.originals[n] ? this.originals[n].file.name : file.name;
    var recrop = this.$('[data-shp-recrop="' + n + '"]');
    if (recrop) recrop.hidden = !(file.shpCropped && this.originals[n]);
    this.$('[data-shp-drop="' + n + '"]').hidden = true;
    this.$('[data-shp-filled="' + n + '"]').hidden = false;
    this.announce(n === 1 ? capitalise(noun) + ' added.' : 'Second ' + noun + ' added.');
    this.view = 'variant';
    this.viewModel = false;

    // Keine Vorschau möglich: Dateityp statt Vorschaubild zeigen, Polaroid auf dem Bild ausblenden
    function noPreview(message) {
      self.setPreviewable(n, false, file);
      self.setMessage(n, message, false);
      if (n === 1) self.setResolutionText(capitalise(noun) + ' added');
      self.renderStage();
    }

    if (isDocument(file)) {
      noPreview('We can’t show a preview of this file here, but we’ll print your logo from it.');
      this.update();
      return;
    }
    this.setPreviewable(n, true, file);

    var img = new Image();
    img.onload = function () {
      if (isSvg(file)) {
        if (n === 1) self.setResolutionText('Vector file – prints sharp at any size');
        return;
      }
      var shortSide = Math.min(img.naturalWidth, img.naturalHeight);
      if (shortSide < MIN_SIDE) {
        self.setMessage(n, 'This ' + noun + ' may be too small to print sharply (' + img.naturalWidth + ' × ' + img.naturalHeight + ' px). ' + (logo ? 'A larger file or an SVG, PDF or AI file gives a better result.' : 'A larger photo gives a better result.'), false);
        if (n === 1) self.setResolutionText(capitalise(noun) + ' added – a larger ' + noun + ' would print sharper');
      } else if (n === 1) {
        self.setResolutionText('Sharp enough to print');
      }
    };
    img.onerror = function () {
      noPreview(isHeic(file)
        ? 'Your browser can’t show a preview of HEIC photos, but we can still print it.'
        : 'We can’t show a preview of this file here, but we’ll print your ' + noun + ' from it.');
    };
    img.src = this.urls[n];

    this.update();
  };

  Configurator.prototype.cropFile = function (n, file, input, rect) {
    var self = this;
    var ratio = String(this.config.crop).split(':').map(Number);
    cropDialog(file, ratio, rect).then(function (res) {
      if (input) input.value = ''; // dasselbe Foto lässt sich so erneut wählen
      if (!res) return;
      self.originals[n] = { file: file, rect: res.rect };
      self.setFile(n, res.file, input || {});
    }, function () {
      if (input) input.value = '';
      self.setMessage(n, 'Your browser can’t open this photo, so you can’t choose a crop here. Please choose a JPG or PNG.', true);
    });
  };

  Configurator.prototype.setPreviewable = function (n, ok, file) {
    this.previewable[n] = ok;
    var thumb = this.$('[data-shp-thumb-img="' + n + '"]');
    var ext = this.$('[data-shp-ext="' + n + '"]');
    var note = this.$('[data-shp-filled-note="' + n + '"]');
    if (thumb) {
      thumb.hidden = !ok;
      if (ok) thumb.src = this.urls[n];
    }
    if (ext) {
      ext.hidden = ok;
      ext.textContent = ((file.name.match(/\.([a-z0-9]+)$/i) || [])[1] || 'File').toUpperCase();
    }
    if (note) note.textContent = ok ? 'Added – shown in your preview' : 'Added – we’ll print from this file';
  };

  Configurator.prototype.clearFile = function (n) {
    if (this.urls[n]) URL.revokeObjectURL(this.urls[n]);
    this.files[n] = null;
    this.urls[n] = null;
    this.originals[n] = null;
    var input = this.$('[data-shp-file="' + n + '"]');
    if (input) input.value = '';
    this.$('[data-shp-drop="' + n + '"]').hidden = false;
    this.$('[data-shp-filled="' + n + '"]').hidden = true;
    this.setMessage(n, '', false);
    this.previewable[n] = true;
    if (this.ai) this.resetFace(n);
    this.announce(n === 1 ? capitalise(this.noun) + ' removed.' : 'Second ' + this.noun + ' removed.');
    this.update();
  };

  Configurator.prototype.setResolutionText = function (text) {
    var el = this.$('[data-shp-check-res]');
    if (el) el.textContent = text;
  };

  Configurator.prototype.renderStage = function (variant) {
    var stage = this.$('[data-shp-stage]');
    if (stage) {
      var src;
      if (this.view === 'variant') {
        var shown = variant || this.variantFor(this.options) || this.minAcrossSizes();
        // Live-Vorschau auf der Socke (sh-sock-preview.js): eigenes erstes Bild mit Polaroid, sobald es eine Vorlage gibt
        src = (this.sock && this.sock.stageSrc()) || (shown && shown.featured_image ? sizedUrl(shown.featured_image.src, 1400) : this.config.fallbackImage);
      } else {
        src = this.view;
      }
      if (src && stage.getAttribute('src') !== src) {
        stage.removeAttribute('srcset');
        stage.src = src;
      }
    }
    var barImg = this.$('[data-shp-bar-img]');
    var current = variant || this.variantFor(this.options) || this.minAcrossSizes();
    if (barImg && current && current.featured_image) {
      var small = sizedUrl(current.featured_image.src, 120);
      if (barImg.getAttribute('src') !== small) { barImg.removeAttribute('srcset'); barImg.src = small; }
    }
    if (this.view === 'variant') {
      this.$$('[data-shp-thumb]').forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-shp-thumb') === 'variant' ? 'true' : 'false'); });
    }
    // Das erste Vorschaubild zeigt immer die gewählte Farbe
    var variantThumb = this.$('[data-shp-variant-thumb]');
    if (variantThumb && current && current.featured_image) {
      var thumbSrc = (this.sock && this.sock.stageSrc()) || sizedUrl(current.featured_image.src, 200);
      if (variantThumb.getAttribute('src') !== thumbSrc) {
        variantThumb.removeAttribute('srcset');
        variantThumb.src = thumbSrc;
      }
    }

    // Model-Foto: Hochformat mit unscharfen Rändern, eigener Hinweis (z. B. Größe des Hoodies)
    var isModel = this.view !== 'variant' && this.viewModel;
    var stageBox = stage ? stage.closest('.shp-stage') : null;
    if (stageBox) {
      stageBox.classList.toggle('is-model', isModel);
      if (isModel) stageBox.style.setProperty('--shp-model-bg', 'url("' + this.view + '")');
    }
    var two = this.needsTwo();
    var hasPhoto = this.config.upload && !!this.files[1];
    var showsPhoto = hasPhoto && this.previewable[1];
    // Gezeichnete Stile: solange gezeichnet wird, Wartekarte über dem Bild, kein Hinweis darunter (2026-10-15)
    var aiWait = showsPhoto && this.view === 'variant' && this.isCartoon() && this.cartoonsBusy();
    var wait = this.$('[data-shp-wait]');
    if (wait) {
      wait.hidden = !aiWait;
      var waitTitle = this.$('[data-shp-wait-title]');
      if (waitTitle && aiWait) waitTitle.textContent = 'We’re drawing ' + this.styleNoun() + ' …';
    }
    var cut = this.$('[data-shp-cutout]');
    if (cut) {
      var ki = this.isCartoon();
      cut.textContent = ki ? cut.getAttribute('data-ki') : cut.getAttribute('data-foto');
      this.$$('[data-shp-cut-ico]').forEach(function (i) { i.hidden = (i.getAttribute('data-shp-cut-ico') === 'ki') !== ki; });
    }
    var noteBox = this.$('[data-shp-notebox]');
    var note = this.$('[data-shp-note]');
    if (noteBox && note) {
      // Ohne eigenes Foto sagt das Label „Beispiel“ alles – der Hinweis erscheint erst mit der eigenen Vorschau
      var modelNote = isModel ? (this.config.modelNote || '') : '';
      var previewNote = this.isCartoon() && this.config.aiPreviewNote ? this.config.aiPreviewNote : this.config.previewNote;
      var noteText = modelNote || (hasPhoto && !isModel && !aiWait ? previewNote : '');
      note.textContent = noteText;
      noteBox.hidden = !noteText;
    }
    if (!this.config.upload) return;

    // Live-Vorschau entsteht: Ladekreis im Label und Lichtstreifen über dem ersten Bild
    var sockBusy = showsPhoto && this.view === 'variant' && !!this.sock && this.sock.busy();
    if (stageBox) stageBox.classList.toggle('is-busy', sockBusy);
    var label = this.$('[data-shp-label]');
    if (label) {
      label.textContent = showsPhoto ? (sockBusy ? 'Creating your preview …' : 'Your preview') : 'Example design';
      label.classList.toggle('is-preview', showsPhoto);
      label.classList.toggle('is-busy', sockBusy);
    }
    for (var n = 1; n <= 2; n++) {
      var pol = this.$('[data-shp-polaroid="' + n + '"]');
      if (!pol) continue;
      // Cartoon gewählt, aber noch kein Foto: Beispiel-Cartoon auf dem Polaroid
      var exImg = this.isCartoon() ? this.$('[data-shp-style-img="' + this.style + '"]') : null;
      var exSrc = exImg ? (exImg.getAttribute('data-example') || exImg.getAttribute('src')) : '';
      var example = n === 1 && !this.files[1] && !!exSrc && this.view === 'variant';
      var show = example || (!!this.files[n] && this.previewable[n] && this.view === 'variant' && (n === 1 || two));
      if (this.sock && this.sock.ownsStage()) show = false;   // das Polaroid ist dann Teil des ersten Bilds
      pol.hidden = !show;
      // Im Cartoon-Stil zeigt das Polaroid den fertigen Cartoon statt des Fotos
      var asCartoon = example || (this.isCartoon() && this.cartoon[n].status === 'done');
      var pimg = this.$('[data-shp-polaroid-img="' + n + '"]');
      var psrc = example ? exSrc : (asCartoon ? this.cartoon[n].url : this.urls[n]);
      if (pimg && show && pimg.getAttribute('src') !== psrc) pimg.src = psrc;
      pol.classList.toggle('is-cartoon', asCartoon);
      var cap = pol.querySelector('figcaption');
      if (cap) {
        if (!cap.getAttribute('data-photo')) cap.setAttribute('data-photo', cap.textContent);
        var noun = this.styleNoun();
        cap.textContent = example ? 'Example: ' + this.styleName() : (asCartoon ? (n === 1 ? capitalise(noun) : 'Second picture') : cap.getAttribute('data-photo'));
      }
    }
    this.checkPreview(showsPhoto, sockBusy, aiWait);
  };

  /* Feste Links „Auf dem Produkt ansehen“ (nur Handy, siehe sh-product.css): beim hochgeladenen Foto und unter dem
     gezeichneten Bild. */
  Configurator.prototype.addViewLinks = function () {
    var self = this;
    this.viewLinks = {};
    [['foto', '[data-shp-filled="1"]'], ['ki', '[data-shp-ai-heads]']].forEach(function (p) {
      var anchor = self.$(p[1]);
      if (!anchor) return;
      var a = document.createElement('button');
      a.type = 'button';
      a.className = 'shp-viewlink';
      a.hidden = true;
      a.innerHTML = '<span>See it on the product</span>' + ARROW_UP;
      anchor.insertAdjacentElement('afterend', a);
      a.addEventListener('click', function () { scrollToStage(self.$('.shp-stage')); });
      self.viewLinks[p[0]] = a;
    });
  };

  /* Vorschau fertig? Dann Links zeigen und einmal je neuem Foto, Stil oder gezeichnetem Bild die Pille (nicht bei
     Farbwechsel). Fertig = eigenes Foto zu sehen, keine Wartekarte, Live-Vorschau mit eigenen Köpfen fertig bzw. alle
     gezeichneten Bilder da. */
  Configurator.prototype.checkPreview = function (showsPhoto, sockBusy, aiWait) {
    var self = this;
    var cartoon = this.isCartoon();
    var ready = showsPhoto && this.view === 'variant' && !sockBusy && !aiWait && (!cartoon || this.cartoonsReady());
    if (ready && this.sock && this.sock.ownsStage()) ready = !!(this.sock.stage && this.sock.stage.eigen && !this.sock.busy());
    var links = this.viewLinks || {};
    if (links.foto) links.foto.hidden = !(ready && !cartoon);
    if (links.ki) links.ki.hidden = !(ready && cartoon);
    if (!ready) return;
    var key = [this.urls[1], this.needsTwo() ? this.urls[2] : '', this.style].concat(cartoon ? this.neededFaces().map(function (n) { return self.cartoon[n].url; }) : []).join('|');
    if (key === this.readyKey) return;
    this.readyKey = key;
    var box = this.$('.shp-stage');
    if (!box) return;
    var r = box.getBoundingClientRect();
    var visible = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0);
    if (visible < r.height * 0.4) showReady(box);
  };

  Configurator.prototype.update = function () {
    var self = this;
    var cfg = this.config;
    this.ensureCartoons();
    var variant = this.variantFor(this.options);
    var sizeMissing = this.sizeMissing();

    // Auswahlknöpfe
    this.$$('[data-opt]').forEach(function (btn) {
      var i = parseInt(btn.getAttribute('data-opt'), 10);
      btn.setAttribute('aria-pressed', self.options[i] === btn.getAttribute('data-value') ? 'true' : 'false');
    });
    var colourName = this.$('[data-shp-colour-name]');
    if (colourName && cfg.colourIndex > -1) colourName.textContent = this.options[cfg.colourIndex] || '';
    var sizeName = this.$('[data-shp-size-name]');
    // „M (130cm x 150cm)“ → „M (130 × 150 cm)“
    if (sizeName && cfg.sizeIndex > -1) sizeName.textContent = sizeMissing ? '' : ': ' + String(this.options[cfg.sizeIndex]).replace(/cm x /g, ' × ').replace(/(\d)cm\)/g, '$1 cm)');

    // Preise auf den Größen-Knöpfen (bei sonst gleicher Auswahl)
    if (cfg.sizeIndex > -1) {
      this.$$('[data-size-price]').forEach(function (el) {
        var opts = self.options.slice();
        opts[cfg.sizeIndex] = el.getAttribute('data-size-price');
        var v = self.variantFor(opts);
        el.textContent = v ? formatMoney(v.price, cfg.moneyFormat) : '';
        el.closest('.shp-size').setAttribute('data-unavailable', v && v.available ? 'false' : 'true');
      });
    }

    // „+2,95 €“ beim zweiten Gesicht
    if (cfg.facesIndex > -1) {
      var values = cfg.facesValues || [];
      this.$$('[data-faces-extra]').forEach(function (el) {
        var value = el.getAttribute('data-faces-extra');
        var idx = values.indexOf(value);
        if (idx < 1) { el.textContent = ''; return; }
        var a = self.options.slice();
        var b = self.options.slice();
        a[cfg.facesIndex] = values[0];
        b[cfg.facesIndex] = value;
        if (cfg.sizeIndex > -1 && !a[cfg.sizeIndex]) {
          var any = self.minAcrossSizes();
          if (any) { a[cfg.sizeIndex] = any.options[cfg.sizeIndex]; b[cfg.sizeIndex] = any.options[cfg.sizeIndex]; }
        }
        var va = self.variantFor(a);
        var vb = self.variantFor(b);
        var diff = va && vb ? vb.price - va.price : 0;
        el.textContent = diff > 0 ? '+' + formatMoney(diff, cfg.moneyFormat) : '';
      });
    }

    // Zweites Foto
    var two = this.needsTwo();
    var slot2 = this.$('[data-shp-slot="2"]');
    if (slot2) slot2.hidden = !two;
    this.$$('[data-shp-slot-label]').forEach(function (el) { el.hidden = !two; });
    var title = this.$('[data-shp-upload-title]');
    if (title) title.textContent = two ? title.getAttribute('data-two') : title.getAttribute('data-one');

    var photosReady = !cfg.upload || (!!this.files[1] && (!two || !!this.files[2]));
    var tips = this.$('[data-shp-tips]');
    var checklist = this.$('[data-shp-checklist]');
    if (tips) tips.hidden = !!this.files[1];
    if (checklist) checklist.hidden = !this.files[1];
    var num = this.$('[data-shp-photo-num]');
    if (num) num.classList.toggle('is-done', photosReady && !!this.files[1]);

    // Preis
    var priceEl = this.$('[data-shp-price]');
    var wasEl = this.$('[data-shp-was]');
    var fromEl = this.$('[data-shp-from]');
    var offEl = this.$('[data-shp-off]');
    var shown = variant || (sizeMissing ? this.minAcrossSizes() : null);
    if (fromEl) fromEl.hidden = !(sizeMissing && !variant);
    if (shown) {
      if (priceEl) priceEl.textContent = formatMoney(shown.price, cfg.moneyFormat);
      var showWas = cfg.showCompare && shown.compare_at_price && shown.compare_at_price > shown.price;
      if (wasEl) {
        wasEl.hidden = !showWas;
        if (showWas) wasEl.textContent = formatMoney(shown.compare_at_price, cfg.moneyFormat);
      }
      if (offEl) {
        offEl.hidden = !showWas;
        if (showWas) offEl.textContent = '−' + Math.floor((1 - shown.price / shown.compare_at_price) * 100) + ' %';
      }
    }
    if (variant) {
      this.$('[data-shp-variant]').value = variant.id;
      if (window.history && window.history.replaceState && !sizeMissing) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    var total = (variant ? variant.price : (shown ? shown.price : 0)) + this.extrasTotal();
    this.$$('[data-shp-total]').forEach(function (el) { el.textContent = formatMoney(total, cfg.moneyFormat); });

    // Kaufbutton (der Button der Kaufleiste bleibt bei fehlender Größe bzw. fehlendem Foto klickbar und springt dorthin)
    var available = !!(variant && variant.available);
    this.missing = null;
    if (sizeMissing) this.missing = 'size';
    else if (available && cfg.upload && !this.files[1]) this.missing = 1;
    else if (available && cfg.upload && two && !this.files[2]) this.missing = 2;
    else if (available && this.isCartoon() && !this.cartoonsReady()) this.missing = 'style';
    var aiBusy = this.isCartoon() && this.cartoonsBusy();
    this.$$('[data-shp-add]').forEach(function (btn) {
      var short = btn.getAttribute('data-short') === 'true';
      if (self.busy) return;
      if (sizeMissing) {
        btn.disabled = !short;
        btn.textContent = 'Choose a size';
      } else if (!variant) {
        btn.disabled = true;
        btn.textContent = 'Unavailable';
      } else if (!available) {
        btn.disabled = true;
        btn.textContent = 'Sold out';
      } else if (cfg.upload && !self.files[1]) {
        btn.disabled = !short;
        btn.textContent = short ? 'Add your ' + self.noun : 'Add your ' + self.noun + ' to continue';
      } else if (cfg.upload && two && !self.files[2]) {
        btn.disabled = !short;
        btn.textContent = short ? 'Add second ' + self.noun : 'Add your second ' + self.noun + ' to continue';
      } else if (self.missing === 'style') {
        btn.disabled = aiBusy && !short;
        btn.textContent = aiBusy ? (short ? 'Drawing …' : 'One moment – drawing your picture …') : 'Check your style';
      } else {
        btn.disabled = false;
        btn.textContent = short ? 'Add to basket' : 'Add to basket · ' + formatMoney(total, cfg.moneyFormat);
      }
    });

    this.renderAi();
    this.renderStage(variant);
    if (this.sock) this.sock.refresh();
  };

  Configurator.prototype.showError = function (text) {
    var el = this.$('[data-shp-error]');
    if (!el) return;
    el.textContent = text || '';
    el.hidden = !text;
  };

  Configurator.prototype.setBusy = function (busy) {
    this.busy = busy;
    this.$$('[data-shp-add]').forEach(function (btn) {
      btn.disabled = busy;
      btn.classList.toggle('is-loading', busy);
      if (busy) btn.textContent = 'Adding to your basket…';
    });
    if (!busy) this.update();
  };

  Configurator.prototype.addToCart = function () {
    var self = this;
    var cfg = this.config;
    var variant = this.variantFor(this.options);
    var two = this.needsTwo();
    if (this.busy || !variant || !variant.available) return;
    if (cfg.upload && (!this.files[1] || (two && !this.files[2]))) return;
    var cartoon = this.isCartoon();
    if (cartoon && !this.cartoonsReady()) return;

    this.showError('');
    this.setBusy(true);
    this.announce('Adding to your basket…');

    var addUrl = (cfg.cartAddUrl || '/cart/add') + '.js';
    var fd = new FormData();
    fd.append('id', String(variant.id));
    fd.append('quantity', '1');
    // Properties as before at SuperHoodie: "Photo" and "Second photo" (logo hoodie too – the producer knows them)
    if (cfg.upload && this.files[1]) fd.append('properties[Photo]', this.files[1], this.files[1].name);
    if (cfg.upload && two && this.files[2]) fd.append('properties[Second photo]', this.files[2], this.files[2].name);
    // Drawn style: the artwork is printed (the photo handover saves it as Photo/Photo2, services/order-files)
    if (cartoon) {
      fd.append('properties[Style]', this.styleName());
      fd.append('properties[Artwork]', this.cartoon[1].blob, this.style + '.png');
      if (two) fd.append('properties[Second artwork]', this.cartoon[2].blob, this.style + '-2.png');
    }

    var chosen = this.$$('[data-shp-extra][aria-pressed="true"]');
    // Extras zusätzlich als Vermerk an der Hauptposition, wie im alten Shop (z. B. „Foto-Freigabe (+ 4€)“)
    chosen.forEach(function (b) {
      var name = b.getAttribute('data-note-name');
      if (name) fd.append('properties[' + name + ']', b.getAttribute('data-note-value') || name);
    });
    var extras = chosen.map(function (b) {
      return { id: parseInt(b.getAttribute('data-variant-id'), 10), quantity: 1 };
    });

    if (window.shAddedToCart) window.shAddedToCart.mark();
    fetch(addUrl, { method: 'POST', body: fd, headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok || data.status) throw new Error(data.description || data.message || 'Something went wrong.');
          return data;
        });
      })
      .then(function () {
        if (!extras.length) return null;
        // Der Hauptartikel liegt schon im Warenkorb; klappen die Extras nicht, bietet der Warenkorb sie erneut an.
        return fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: extras })
        }).catch(function () { return null; });
      })
      .then(function () {
        // Klaviyo and Elevar need a moment to send "Added to Cart" (snippets/sh-added-to-cart.liquid).
        return window.shAddedToCart ? window.shAddedToCart() : null;
      })
      .then(function () {
        window.location.href = cfg.cartUrl || '/cart';
      })
      .catch(function (err) {
        self.setBusy(false);
        self.showError((err && err.message) ? err.message + ' Please try again.' : 'Sorry, we couldn’t add this to your basket. Please try again.');
      });
  };

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-shp]'), function (root) {
      if (root.__shp) return;
      root.__shp = new Configurator(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
